import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Caret } from '@/shared/components/ui/Caret'
import { createPortal } from 'react-dom';
import { useT } from '@/shared/i18n/useT';

interface CopySplitButtonProps {
  onCopy: () => void;
  copyHeaders: boolean;
  onToggleCopyHeaders: (value: boolean) => void;
  copyNumberFormat: boolean;
  onToggleCopyNumberFormat: (value: boolean) => void;
  copyFormulas: boolean;
  onToggleCopyFormulas: (value: boolean) => void;
  hasSelection?: boolean;
  onCopySelection?: (asMarkdown?: boolean) => void;
  onCopyAll?: (asMarkdown?: boolean) => void;
  disabled?: boolean;
  /** Only the options caret, for a bar that draws its own copy button (the phone selection bar). */
  caretOnly?: boolean;
}

export function CopySplitButton({
  onCopy,
  copyHeaders,
  onToggleCopyHeaders,
  copyNumberFormat,
  onToggleCopyNumberFormat,
  copyFormulas,
  onToggleCopyFormulas,
  hasSelection = false,
  onCopySelection,
  onCopyAll,
  disabled = false,
  caretOnly = false,
}: CopySplitButtonProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 8, top: 8 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const copyLabel = hasSelection ? t('grid.copySelection') : t('grid.copyAll');
  const menuLabel = t('grid.copyMenu');

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const updatePosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 200;
      setMenuPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - menuWidth - 8)),
        top: rect.bottom + 4,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const handleCopySelection = (asMarkdown = false) => {
    setOpen(false);
    if (onCopySelection) onCopySelection(asMarkdown);
    else onCopy();
  };

  const handleCopyAll = (asMarkdown = false) => {
    setOpen(false);
    if (onCopyAll) onCopyAll(asMarkdown);
    else onCopy();
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative inline-flex ${caretOnly ? 'h-10' : 'h-7'} shrink-0 overflow-hidden rounded text-fg`}
    >
      {!caretOnly && <button
        type="button"
        aria-label={copyLabel}
        title={copyLabel}
        disabled={disabled}
        onClick={onCopy}
        className="flex h-7 items-center justify-center pl-1.5 pr-0.5 transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:opacity-40"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      </button>}
      <button
        type="button"
        aria-label={menuLabel}
        title={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex ${caretOnly ? 'h-10 w-5 justify-center rounded-lg text-text-muted' : 'h-7 w-4 justify-start'} items-center transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:opacity-40`}
      >
        <Caret />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={menuLabel}
            style={{ position: 'fixed', left: menuPosition.left, top: menuPosition.top }}
            className="z-50 min-w-48 max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-border bg-surface p-1 text-xs shadow-xl flex flex-col gap-0.5"
          >
            {hasSelection && (
              <button
                type="button"
                role="menuitem"
                onClick={() => handleCopySelection(false)}
                className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none"
              >
                <span className="text-xs font-normal text-fg">{t('grid.copySelection')}</span>
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => handleCopyAll(false)}
              className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none"
            >
              <span className="text-xs font-normal text-fg">{t('grid.copyAll')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => handleCopyAll(true)}
              className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none"
            >
              <span className="text-xs font-normal text-fg">{t('grid.copyAllMd')}</span>
            </button>
            <div className="my-1 border-t border-border" />
            <label className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3">
              <input
                type="checkbox"
                checked={copyHeaders}
                onChange={(e) => onToggleCopyHeaders(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-brand focus:ring-1 focus:ring-focus"
              />
              <span className="text-xs font-normal text-fg">{t('grid.copyHeaders')}</span>
            </label>
            <label className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3">
              <input
                type="checkbox"
                checked={copyNumberFormat}
                onChange={(e) => onToggleCopyNumberFormat(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-brand focus:ring-1 focus:ring-focus"
              />
              <span className="text-xs font-normal text-fg">{t('grid.copyNumberFormat')}</span>
            </label>
            <div
              role="radiogroup"
              aria-label={t('grid.copyFormulasLabel')}
              className="flex items-center gap-2 px-2.5 py-1.5"
            >
              <span className="text-xs font-normal text-fg">{t('grid.copyFormulasLabel')}</span>
              <div className="ml-auto flex rounded-full border border-border p-0.5">
                {([true, false] as const).map((formulas) => {
                  const selected = copyFormulas === formulas;
                  return (
                    <button
                      key={String(formulas)}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onToggleCopyFormulas(formulas)}
                      className={[
                        'rounded-full px-2 py-0.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                        selected ? 'bg-accent-soft font-medium text-fg' : 'text-muted hover:text-fg',
                      ].join(' ')}
                    >
                      {formulas ? t('grid.copyFormulas') : t('grid.copyValues')}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
